import type { PackBuilderGroup, PackBuilderHealth, PackBuilderPack } from '../../lib/api/operatorConsole';
import type { PillTone } from '../../components/ui/Pill';

export function formatMoney(amount: number | null | undefined, currency: string | null): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return `${amount.toFixed(2)}${currency ? ` ${currency}` : ''}`;
}

export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** "25..50" → "25 – 50", "100..Infinity" → "100+". */
export function formatRange(range: string): string {
  const [from, to] = range.split('..');
  if (from === undefined || to === undefined) return range;
  return to === 'Infinity' ? `${from}+` : `${from} – ${to}`;
}

export function groupLabel(group: PackBuilderGroup): string {
  return group.to === null ? `${group.from}+` : `${group.from} – ${group.to}`;
}

/** The backend key for a group, the same string the validator's buckets use. */
export function groupRangeKey(group: PackBuilderGroup): string {
  return `${group.from}..${group.to === null ? 'Infinity' : group.to}`;
}

export function packStatusLabel(pack: Pick<PackBuilderPack, 'status' | 'availability'>): {
  label: string;
  tone: PillTone;
} {
  if (pack.availability === 'restocking' || (pack.availability === 'enabled' && pack.status === 'restocking')) {
    return { label: 'Restocking', tone: 'info' };
  }
  if (pack.availability === 'enabled') return { label: 'Live', tone: 'success' };
  // `disabled` and `default` (and any value the backend adds later) are set by
  // support, not by the operator: never show them as a publishable draft.
  if (pack.availability !== 'staged') return { label: 'Disabled', tone: 'error' };
  return { label: 'Not published', tone: 'warning' };
}

/** Published from the operator's point of view (CONTRACT §4.8): the owner
 *  config is enabled OR restocking. Both lock price/RTP/groups and forbid
 *  Delete; only `enabled` can be unpublished. */
export function isLive(pack: Pick<PackBuilderPack, 'availability'>): boolean {
  return pack.availability === 'enabled' || pack.availability === 'restocking';
}

/** The only availability the operator owns. The enum has five values
 *  ({default, disabled, enabled, restocking, staged}) and support can set
 *  `disabled` from the admin Own-packs table; only `staged` may be published,
 *  edited or deleted — everything else is read-only from the console. */
export function isStaged(pack: Pick<PackBuilderPack, 'availability'>): boolean {
  return pack.availability === 'staged';
}

/** Support took the pack away from the operator (`disabled`, `default`, or any
 *  value the backend adds later). EVERY write entrance answers 409
 *  PACK_SUPPORT_DISABLED for it, so no surface may offer one. */
export function isSupportDisabled(pack: Pick<PackBuilderPack, 'availability'>): boolean {
  return !isStaged(pack) && !isLive(pack);
}

export const SUPPORT_DISABLED_HINT =
  'Support has disabled this pack — contact support to change it.';

export function healthLabel(health: PackBuilderHealth | null): { label: string; tone: PillTone } {
  if (!health) return { label: 'Not checked', tone: 'neutral' };
  if (!health.ok) {
    return {
      label: `${health.errors_count} ${health.errors_count === 1 ? 'issue' : 'issues'}`,
      tone: 'error',
    };
  }
  if (health.warnings_count > 0) {
    return {
      label: `${health.warnings_count} ${health.warnings_count === 1 ? 'warning' : 'warnings'}`,
      tone: 'warning',
    };
  }
  return { label: 'Healthy', tone: 'success' };
}

/** LinkPolicy::REASONS + LinkItems' own three (CONTRACT §4.10). Unknown
 *  reasons fall through untranslated. */
const SKIP_REASON_LABELS: Record<string, string> = {
  different_pool: 'not in the pack builder pool',
  not_available: 'not available',
  foreign_operator_item: 'owned by another operator',
  grade_mismatch: 'grade type does not match the pack',
  not_found: 'card no longer exists',
  already_in_pack: 'already in the pack',
  not_in_pack: 'not in the pack',
};

export function skipReasonLabel(reason: string): string {
  return SKIP_REASON_LABELS[reason] ?? 'not eligible for this pack';
}

/** Solver diagnostics differ between v1 and v2 — only trust numbers. */
export function diagnosticNumber(diagnostics: Record<string, unknown>, key: string): number | null {
  const value = diagnostics[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
